/**
 * Tales story format, schema version 1.
 *
 * A SERIES is a folder with `series.json` and one folder per EPISODE.
 * An EPISODE is a directed graph of NODES. Playback starts at `start` and
 * follows `choices` (player picks) or `next` (automatic) until it reaches an
 * `ending` node. Branching is kept tractable with shared "bottleneck" nodes;
 * consequences that must survive a bottleneck live in VARIABLES (flags and
 * counters) that choices set and later nodes/choices can test.
 *
 * Design rules encoded here:
 *  - Every playable beat is a portrait (9:16) video clip; a node may be
 *    produced from several generated SHOTS (5–10 s each) concatenated.
 *  - A node either has `choices` (2–4), or `next`, or is an `ending`.
 *  - `switch` nodes have no video: they route on state (Telltale-style
 *    "X will remember that" payoffs) and are invisible to the player.
 */
import { z } from 'zod';

export const SCHEMA_VERSION = 1 as const;

export const idSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/i, 'ids are alphanumeric with _ or -');

// ---------- variables & conditions ----------

/** `scope: 'series'` variables persist across episodes (seeded from saved series state). */
const variableScope = z.enum(['episode', 'series']).default('episode');

export const variableDefSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bool'), initial: z.boolean().default(false), label: z.string().optional(), scope: variableScope }),
  z.object({
    type: z.literal('int'),
    initial: z.number().int().default(0),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    label: z.string().optional(),
    scope: variableScope,
  }),
]);
export type VariableDef = z.infer<typeof variableDefSchema>;

export const comparisonOpSchema = z.enum(['==', '!=', '<', '<=', '>', '>=']);

/** A condition tree evaluated against the current variables. */
export type Condition =
  | { var: string; op?: z.infer<typeof comparisonOpSchema> | undefined; value?: number | boolean | undefined }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.object({
      var: idSchema,
      op: comparisonOpSchema.optional(),
      value: z.union([z.number(), z.boolean()]).optional(),
    }),
    z.object({ all: z.array(conditionSchema).min(1) }),
    z.object({ any: z.array(conditionSchema).min(1) }),
    z.object({ not: conditionSchema }),
  ]),
);

export const effectSchema = z.union([
  z.object({ set: idSchema, value: z.union([z.number().int(), z.boolean()]) }),
  z.object({ add: idSchema, value: z.number().int() }),
]);
export type Effect = z.infer<typeof effectSchema>;

// ---------- media & production ----------

export const shotSchema = z.object({
  id: idSchema,
  /** Generation prompt (scene, action, camera, light, audio line). */
  prompt: z.string().min(1),
  /** Target duration in seconds for the generated clip. */
  duration: z.number().int().min(3).max(30).default(8),
  /** Keys into the series bible (characters/locations) passed as reference images. */
  references: z.array(z.string()).max(7).default([]),
  /** Optional first-frame still (bible key or asset id). */
  firstFrame: z.string().optional(),
  model: z.string().optional(),
  notes: z.string().optional(),
});
export type Shot = z.infer<typeof shotSchema>;

export const videoSchema = z.object({
  /** Path relative to the episode folder, e.g. media/n01.mp4 */
  src: z.string().min(1),
  poster: z.string().optional(),
  /** Duration in seconds (filled by the media tool; used for UI only). */
  duration: z.number().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type Video = z.infer<typeof videoSchema>;

// ---------- transitions ----------

/** Where to go next: a node id, or an ordered list of conditional targets (first match wins; last entry should be unconditional). */
export const targetSchema = z.union([
  idSchema,
  z.array(z.object({ when: conditionSchema.optional(), to: idSchema })).min(1),
]);
export type Target = z.infer<typeof targetSchema>;

export const choiceSchema = z.object({
  id: idSchema,
  /** Short, 2–6 words, imperative. */
  text: z.string().min(1).max(60),
  /** Optional one-line hint shown smaller under the text. */
  hint: z.string().max(80).optional(),
  to: targetSchema,
  /** Show this choice only when the condition holds. */
  when: conditionSchema.optional(),
  effects: z.array(effectSchema).default([]),
  /** Analytics tag, e.g. "risky" | "safe" | "romantic". */
  tag: z.string().optional(),
});
export type Choice = z.infer<typeof choiceSchema>;

// ---------- nodes ----------

const nodeBase = {
  title: z.string().min(1).max(120),
  /** One-line beat description for editors and AI tools. */
  summary: z.string().max(500).optional(),
  tags: z.array(z.string()).default([]),
  /** Applied when the node is entered. */
  effects: z.array(effectSchema).default([]),
};

export const videoNodeSchema = z.object({
  type: z.literal('video'),
  ...nodeBase,
  video: videoSchema.optional(),
  shots: z.array(shotSchema).default([]),
  /** Dialogue / VO transcript for subtitles & review. */
  transcript: z.string().optional(),
  choices: z.array(choiceSchema).min(2).max(4).optional(),
  /** Seconds the player has to decide (undefined = untimed). */
  timer: z.number().int().min(3).max(60).optional(),
  /** Choice id picked when the timer runs out. Required when timer is set. */
  defaultChoice: idSchema.optional(),
  /** Overlay the choices this many seconds before the clip ends (default 0 = at end). */
  choiceLeadIn: z.number().min(0).max(10).default(0),
  next: targetSchema.optional(),
});

export const switchNodeSchema = z.object({
  type: z.literal('switch'),
  ...nodeBase,
  next: targetSchema,
});

export const endingNodeSchema = z.object({
  type: z.literal('ending'),
  ...nodeBase,
  video: videoSchema.optional(),
  shots: z.array(shotSchema).default([]),
  transcript: z.string().optional(),
  /** Stable id shown to players ("Ending 2 of 3") and used by analytics. */
  endingId: idSchema,
  /** Player-facing name of the ending, e.g. "The Clean Getaway". */
  endingTitle: z.string().min(1).max(80),
  /** Player-facing one-liner shown on the ending card. */
  endingText: z.string().max(240).optional(),
  /** `cliffhanger` continues to the next episode; `final` closes the series branch. */
  kind: z.enum(['cliffhanger', 'final']).default('cliffhanger'),
});

export const nodeSchema = z.discriminatedUnion('type', [videoNodeSchema, switchNodeSchema, endingNodeSchema]);
export type VideoNode = z.infer<typeof videoNodeSchema>;
export type SwitchNode = z.infer<typeof switchNodeSchema>;
export type EndingNode = z.infer<typeof endingNodeSchema>;
export type StoryNode = z.infer<typeof nodeSchema>;

/** Structural invariants of a video node that zod's discriminated union cannot express. */
export function videoNodeIssues(n: VideoNode): string[] {
  const issues: string[] = [];
  if (n.choices && n.next) issues.push('video node cannot have both choices and next');
  if (!n.choices && !n.next) issues.push('video node needs choices or next');
  if (n.timer !== undefined && !n.defaultChoice) issues.push('timer requires defaultChoice');
  if (n.defaultChoice && !n.choices?.some((c) => c.id === n.defaultChoice))
    issues.push(`defaultChoice ${n.defaultChoice} is not one of the choices`);
  const ids = new Set<string>();
  for (const c of n.choices ?? []) {
    if (ids.has(c.id)) issues.push(`duplicate choice id ${c.id}`);
    ids.add(c.id);
  }
  return issues;
}

// ---------- episode & series ----------

export const episodeSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: idSchema,
  title: z.string().min(1).max(120),
  /** Shown in catalog/episode list. */
  synopsis: z.string().max(600).optional(),
  start: idSchema,
  variables: z.record(idSchema, variableDefSchema).default({}),
  nodes: z.record(idSchema, nodeSchema),
  /** Loose, human/AI-readable production notes. */
  notes: z.string().optional(),
});
export type Episode = z.infer<typeof episodeSchema>;

export const characterSchema = z.object({
  name: z.string(),
  /** Short visual signature kept byte-identical across prompts. */
  look: z.string(),
  voice: z.string().optional(),
  /** Reference stills: paths relative to series folder or Scenario asset ids. */
  references: z.array(z.string()).default([]),
});

export const locationSchema = z.object({
  name: z.string(),
  look: z.string(),
  references: z.array(z.string()).default([]),
});

export const seriesSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: idSchema,
  title: z.string().min(1).max(120),
  tagline: z.string().max(140).optional(),
  synopsis: z.string().max(1000).optional(),
  genres: z.array(z.string()).default([]),
  /** Content rating, e.g. "12+", "16+". */
  rating: z.string().optional(),
  language: z.string().default('en'),
  cover: z.string().optional(),
  /** Shared visual style line prepended to every shot prompt. */
  style: z.string().optional(),
  characters: z.record(idSchema, characterSchema).default({}),
  locations: z.record(idSchema, locationSchema).default({}),
  episodes: z
    .array(
      z.object({
        id: idSchema,
        /** Folder name relative to episodes/ (defaults to id). */
        dir: z.string().optional(),
        /** Credits needed to unlock; 0 = free. */
        price: z.number().int().min(0).default(0),
        status: z.enum(['draft', 'review', 'published']).default('draft'),
      }),
    )
    .min(1),
  /** Number of leading episodes that are always free (overrides per-episode price when set). */
  freeEpisodes: z.number().int().min(0).optional(),
  /** Catalog ordering (lower first); the first series is the home hero. */
  order: z.number().int().default(100),
});
export type Series = z.infer<typeof seriesSchema>;
export type Character = z.infer<typeof characterSchema>;
export type Location = z.infer<typeof locationSchema>;

// ---------- runtime manifest (what the player downloads) ----------

export const catalogEntrySchema = z.object({
  id: idSchema,
  title: z.string(),
  tagline: z.string().optional(),
  synopsis: z.string().optional(),
  genres: z.array(z.string()),
  cover: z.string().optional(),
  episodes: z.array(z.object({ id: idSchema, title: z.string(), price: z.number().int(), synopsis: z.string().optional() })),
});
export const catalogSchema = z.object({ generatedAt: z.string(), series: z.array(catalogEntrySchema) });
export type Catalog = z.infer<typeof catalogSchema>;
export type CatalogEntry = z.infer<typeof catalogEntrySchema>;
